import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { CreateButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { Dispatch, useCallback, useEffect, useState } from 'react'
import { useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import {
  PrinciplesConfigurationForm,
  PrinciplesConfigurationFormValues,
} from 'src/screens/administration/configuration-management/principles-configuration/principles/edit/PrinciplesConfigurationForm'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { Mutation_Root, Principle_Config_Insert_Input } from 'src/@types/graphql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { insertPrincipleConfig } from 'src/screens/administration/configuration-management/principles-configuration/principles/principlesConfigQueries'

const defaultInitialValues: PrinciplesConfigurationFormValues = {
  enName: '',
  deName: '',
  frName: '',
  itName: '',
  status: CONFIGURATION_STATUS.ACTIVE,
  enHelpInstructions: '',
  deHelpInstructions: '',
  frHelpInstructions: '',
  itHelpInstructions: '',
  helpAndInstructionsKey: '',
}

const onCreate = async () => {
  document.getElementById('principles-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const PrinciplesConfigurationPrinciplesCreatePage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()

  const [initialValues, setInitialValues] = useState<PrinciplesConfigurationFormValues | null>(null)

  useEffect(() => {
    setInitialValues(defaultInitialValues)
  }, [])

  const onBackHandler = useCallback(() => {
    navigate(ROUTES.PrinciplesConfigurationIndex.nested.PrinciplesConfigurationPrinciples.params({}))
  }, [navigate])

  const handleSubmitLocal = useCallback(
    async (values: PrinciplesConfigurationFormValues, setFormDirty: Dispatch<boolean>) => {
      const principleInsertInput: Principle_Config_Insert_Input = {
        code: crypto.randomUUID(),
        names: {
          de: values.deName ?? '',
          en: values.enName ?? '',
          fr: values.frName ?? '',
          it: values.itName ?? '',
        },
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        help_and_instructions: {
          de: values.deHelpInstructions ? HtmlSanitizer.sanitize(values.deHelpInstructions) : '',
          en: values.enHelpInstructions ? HtmlSanitizer.sanitize(values.enHelpInstructions) : '',
          fr: values.frHelpInstructions ? HtmlSanitizer.sanitize(values.frHelpInstructions) : '',
          it: values.itHelpInstructions ? HtmlSanitizer.sanitize(values.itHelpInstructions) : '',
        },
      }

      const { data, error } = await urqlClient
        .mutation<{
          insert_principle_config: Mutation_Root['insert_principle_config']
        }>(insertPrincipleConfig, {
          principleInsertInput: [principleInsertInput],
        })
        .toPromise()

      if (!error && data?.insert_principle_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setFormDirty(false)
        onBackHandler()
      } else {
        notificationService.operationFailed()
      }
    },
    [notificationService, onBackHandler, urqlClient],
  )

  return (
    <ScreenLayout
      title={getMessage('label.configuration.principles.create')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={<CreateButton origin="header" onClick={onCreate} />}
    >
      <PageLayout>
        <>
          {initialValues && (
            <PrinciplesConfigurationForm mode="CREATE" onSubmit={handleSubmitLocal} initialValues={initialValues} />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
